import { updateSlideInDeck } from "../editor/deckEditor.js";
export const FLAT_ILLUSTRATION_STYLE = "flat vector editorial illustration, limited palette, consistent cross-slide style, no readable text";
export const PHOTOREAL_ILLUSTRATION_STYLE = "cinematic photoreal mood, cohesive lighting, limited palette accents, no readable text";
const IMPACT_ROLES = new Set(["cover", "section", "cta", "summary"]);
export function defaultArtDirection(kind = "illustrative") {
    const isPhoto = kind === "photoreal";
    return {
        kind: isPhoto ? "photoreal" : "flat",
        illustrationStyle: isPhoto ? PHOTOREAL_ILLUSTRATION_STYLE : FLAT_ILLUSTRATION_STYLE,
        paletteLock: true,
    };
}
export function getArtDirection(style) {
    if (style.artDirection?.illustrationStyle) {
        return {
            kind: style.artDirection.kind === "photoreal" ? "photoreal" : "flat",
            illustrationStyle: style.artDirection.illustrationStyle,
            paletteLock: style.artDirection.paletteLock !== false,
            seedRefS3Key: style.artDirection.seedRefS3Key?.trim() || undefined,
        };
    }
    return defaultArtDirection(style.backgroundRealism);
}
export function isFlatArtDirection(style) {
    return getArtDirection(style).kind === "flat";
}
export function slideUsesImpactBackground(slide) {
    const role = (slide.outlineRole ?? "").toLowerCase();
    return IMPACT_ROLES.has(role);
}
/** F20 — role-based background: cover/section impact image; content slides flat + spot illustration. */
export function resolveSlideVisualPlan(slide, theme, brief) {
    const subject = brief.subject?.trim() || "abstract concept";
    const wantsContent = brief.imageRole === "content" ||
        slide.outlineVisual?.kind === "image" ||
        slide.outlineVisual?.kind === "graphic";
    if (isFlatArtDirection(theme) && !slideUsesImpactBackground(slide)) {
        if (wantsContent)
            return { kind: "flat-plus-spot", subject };
        return { kind: "flat-only" };
    }
    if (brief.imageRole === "none" && !slideUsesImpactBackground(slide)) {
        return isFlatArtDirection(theme) ? { kind: "flat-only" } : { kind: "impact-background", subject };
    }
    if (brief.imageRole === "content" || wantsContent) {
        if (isFlatArtDirection(theme) && !slideUsesImpactBackground(slide)) {
            return { kind: "flat-plus-spot", subject };
        }
    }
    return { kind: "impact-background", subject };
}
export function artDirectionPromptLines(style, mode) {
    const ad = getArtDirection(style);
    const lines = [
        `Art direction (${ad.kind}): ${ad.illustrationStyle}.`,
        `Locked palette — surface ${style.palette.surface}, accent ${style.palette.accent}, muted ${style.palette.muted}.`,
    ];
    if (ad.kind === "flat") {
        lines.push(mode === "content"
            ? "Flat vector spot illustration, same visual language on every slide, no text in image."
            : "Flat editorial full-bleed background, calm areas for text, no readable typography.");
    }
    if (ad.seedRefS3Key) {
        lines.push(`Match style reference asset: ${ad.seedRefS3Key}.`);
    }
    return lines;
}
export function setSlideFlatBackground(deck, slideId) {
    return updateSlideInDeck(deck, slideId, (slide) => ({
        ...slide,
        background: {
            kind: "solid",
            prompt: "flat palette surface (F20)",
        },
    }));
}
export function withArtDirection(style, patch) {
    const current = getArtDirection(style);
    return {
        ...style,
        artDirection: {
            ...current,
            ...patch,
            illustrationStyle: patch.illustrationStyle?.trim() || current.illustrationStyle,
        },
    };
}
